import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import OutsideClickHandler from "react-outside-click-handler";
import { HiChevronDown } from "react-icons/hi";
import { useGlobalContext } from "./context/AuthContext";
import Profile from "./auth/Profile";

type NavLinkItem = {
  label: string;
  to: string;
  isLab?: boolean;
};

const primaryLinks: NavLinkItem[] = [
  { label: "Home", to: "/home" },
  { label: "About", to: "/about" },
];

const exploreLinks: NavLinkItem[] = [
  { label: "Browse", to: "/subject-single" },
  { label: "Drafts", to: "/workspace/drafts" },
  { label: "Navigator", to: "/navigator" },
  { label: "Map", to: "/" },
];

const labsLinks: NavLinkItem[] = [
  { label: "Gists", to: "/gists", isLab: true },
  { label: "Bulk Authoring", to: "/workspace/bulk-authoring", isLab: true },
  { label: "Subject Items", to: "/subject-items", isLab: true },
  { label: "Design Lab", to: "/design-lab", isLab: true },
  { label: "Gist Lab", to: "/gist-lab", isLab: true },
  { label: "Subjects Lab", to: "/subjects-lab", isLab: true },
  { label: "Comment Lab", to: "/comment-lab", isLab: true },
];

function isExplorePath(pathname: string): boolean {
  return pathname === "/" || pathname === "/subject-single" || pathname === "/workspace/drafts" || pathname === "/navigator";
}

const Navbar: React.FC = () => {
  const { user } = useGlobalContext();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isExploreOpen, setIsExploreOpen] = useState(false);
  const [isLabsOpen, setIsLabsOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsExploreOpen(false);
    setIsLabsOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      document.body.classList.remove("site-navbar-menu-open");
      return;
    }

    document.body.classList.add("site-navbar-menu-open");

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
        setIsExploreOpen(false);
        setIsLabsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.classList.remove("site-navbar-menu-open");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExploreOpen(false);
        setIsLabsOpen(false);
      }
    };

    if (isExploreOpen || isLabsOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isExploreOpen, isLabsOpen]);

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsExploreOpen(false);
    setIsLabsOpen(false);
  }, []);

  const handleMobilePanelClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    if (target.closest("a, button")) {
      closeMobileMenu();
    }
  }, [closeMobileMenu]);

  const renderLink = (link: NavLinkItem, className = "site-navbar-link") => (
    <Link
      key={link.to}
      to={link.to}
      className={`${className}${link.isLab ? " site-navbar-lab-link" : ""}`}
      onClick={closeMobileMenu}
    >
      {link.label}
    </Link>
  );

  const renderDropdownItem = (link: NavLinkItem, onClick: () => void) => (
    <Link
      key={link.to}
      to={link.to}
      className="site-navbar-dropdown-item"
      role="menuitem"
      onClick={onClick}
    >
      {link.label}
    </Link>
  );

  return (
    <nav className="site-navbar" aria-label="Primary navigation">
      <Link to="/home" className="site-navbar-brand" onClick={closeMobileMenu}>
        <span className="site-navbar-brand-dot" />
        <span>Gistoscope</span>
      </Link>

      <div className="site-navbar-links" aria-label="Desktop navigation links">
        {renderLink(primaryLinks[0])}

        <div className="site-navbar-dropdown-wrap">
          <OutsideClickHandler onOutsideClick={() => setIsExploreOpen(false)}>
            <button
              type="button"
              className={`site-navbar-link site-navbar-dropdown-toggle ${(isExploreOpen || isExplorePath(location.pathname)) ? "is-active" : ""}`}
              onClick={() => {
                setIsExploreOpen((current) => !current);
                setIsLabsOpen(false);
              }}
              aria-haspopup="menu"
              aria-expanded={isExploreOpen}
              aria-controls="explore-dropdown-menu"
            >
              Explore
              <HiChevronDown className={`site-navbar-dropdown-chevron ${isExploreOpen ? "is-rotated" : ""}`} />
            </button>

            {isExploreOpen && (
              <div id="explore-dropdown-menu" className="site-navbar-dropdown-panel" role="menu">
                {exploreLinks.map((link) => renderDropdownItem(link, () => setIsExploreOpen(false)))}
              </div>
            )}
          </OutsideClickHandler>
        </div>

        {renderLink(primaryLinks[1])}
        
        <div className="site-navbar-dropdown-wrap">
          <OutsideClickHandler onOutsideClick={() => setIsLabsOpen(false)}>
            <button
              type="button"
              className={`site-navbar-link site-navbar-lab-link site-navbar-dropdown-toggle ${isLabsOpen ? "is-active" : ""}`}
              onClick={() => {
                setIsLabsOpen((current) => !current);
                setIsExploreOpen(false);
              }}
              aria-haspopup="menu"
              aria-expanded={isLabsOpen}
              aria-controls="labs-dropdown-menu"
            >
              Labs
              <HiChevronDown className={`site-navbar-dropdown-chevron ${isLabsOpen ? "is-rotated" : ""}`} />
            </button>
            
            {isLabsOpen && (
              <div id="labs-dropdown-menu" className="site-navbar-dropdown-panel" role="menu">
                {labsLinks.map((link) => renderDropdownItem(link, () => setIsLabsOpen(false)))}
              </div>
            )}
          </OutsideClickHandler>
        </div>
      </div>

      <div className="site-navbar-user site-navbar-user-desktop">
        {user ? (
          <div className="site-navbar-user-copy">
            <span className="site-navbar-welcome">
              Welcome, <span className="site-navbar-user-name">{user.name}</span>
              {user.userType === "admin" && <span className="site-navbar-admin-badge">Admin</span>}
            </span>
            <Profile />
          </div>
        ) : (
          <Link to="/login" className="site-navbar-login">
            Login
          </Link>
        )}
      </div>

      <button
        type="button"
        className="site-navbar-menu-button"
        aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
        aria-controls="site-mobile-navigation"
        aria-expanded={isMobileMenuOpen}
        onClick={() => setIsMobileMenuOpen((current) => !current)}
      >
        <span className="site-navbar-menu-line" />
        <span className="site-navbar-menu-line" />
        <span className="site-navbar-menu-line" />
      </button>

      {isMobileMenuOpen && (
        <button
          type="button"
          className="site-navbar-backdrop"
          aria-label="Close navigation menu"
          onClick={closeMobileMenu}
          onPointerDown={closeMobileMenu}
        />
      )}

      <div
        id="site-mobile-navigation"
        className={`site-navbar-mobile-panel${isMobileMenuOpen ? " is-open" : ""}`}
        aria-hidden={!isMobileMenuOpen}
        onClickCapture={handleMobilePanelClickCapture}
      >
        <div className="site-navbar-mobile-section">
          <div className="site-navbar-mobile-heading">Primary</div>
          <div className="site-navbar-mobile-links">
            {primaryLinks.map((link) => renderLink(link, "site-navbar-mobile-link"))}
          </div>
        </div>

        <div className="site-navbar-mobile-section">
          <div className="site-navbar-mobile-heading">Explore</div>
          <div className="site-navbar-mobile-links">
            {exploreLinks.map((link) => renderLink(link, "site-navbar-mobile-link"))}
          </div>
        </div>

        <div className="site-navbar-mobile-section">
          <div className="site-navbar-mobile-heading">Labs</div>
          <div className="site-navbar-mobile-links">
            {labsLinks.map((link) => renderLink(link, "site-navbar-mobile-link"))}
          </div>
        </div>

        <div className="site-navbar-mobile-section site-navbar-mobile-account">
          <div className="site-navbar-mobile-heading">Account</div>
          {user ? (
            <div className="site-navbar-mobile-user-copy">
              <span className="site-navbar-mobile-user-name">{user.name}</span>
              {user.userType === "admin" && <span className="site-navbar-admin-badge">Admin</span>}
              <Profile />
            </div>
          ) : (
            <Link to="/login" className="site-navbar-mobile-login" onClick={closeMobileMenu}>
              Login
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
